import { withNamespaces } from "react-i18next";
import { Card, CardBody, CardHeader, Col, Container, Label, Row, UncontrolledTooltip } from "reactstrap";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import Navigator from "src/components/Navigator";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import { toast } from "react-toastify";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';

const LoadingComponent = () => (
    <Row>
        <Col>
            <Card>
                <CardHeader className="bg-white">
                    <p
                        style={{ width: '20%', height: '20px' }}
                        className="dt-field dt-skeleton mb-1"
                    />
                    <p
                        style={{ width: '40%', height: '20px' }}
                        className="dt-field dt-skeleton"
                    />
                </CardHeader>

                <CardBody>
                    <Row
                        className="mb-3"
                    >
                        <Col sm="12" md="4">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                        <Col sm="12" md="4">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                        <Col sm="12" md="4">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                    </Row>

                    <Row
                        className="mb-3"
                    >
                        <Col sm="12" md="4">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                        <Col sm="12" md="4">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                        <Col sm="12" md="4">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col sm="12" md="12">
                            <p
                                style={{ width: '20%', height: '20px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                            <p
                                style={{ width: '40%', height: '100px' }}
                                className="dt-field dt-skeleton mb-1"
                            />
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Col>
    </Row>
)

const ProductDetailPage = ({ t }) => {
    const hasEnitityFeature = isFeatureEnabledForActiveModule('entity_management');
    const params = useParams();
    const productId = params.id;

    const dateUtils = new DateUtils();

    const fetchProductDetailsQuery = useQuery({
        queryKey: [
            '3rd-party-management-eudr-product-details',
            productId
        ],
        queryFn: async () => {
            const service = SupplierProductService.getInstance();

            return await service.show(productId, {});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: true,
        onError: () => {
            toast(t('An error occurred while fetching product details.'), {
                type: 'error'
            });
        }
    });

    const productData = fetchProductDetailsQuery.data || {};
        
    const commodity = productData?.commodity;

    return (
        <div className='page-content'>
            <Container fluid>
                <div className="product-details-page">
                    <Row>
                        <Col lg="12" className="mb-4">
                            <Navigator
                                backButtonOptions={(props) => {
                                    return {
                                        ...props,
                                        enable: true
                                    }
                                }}
                                breadCrumbs={[
                                    {
                                        title: t('SIP'),
                                        link: '/third-party/3rd-party/home'
                                    },
                                    {
                                        title: t('Products'),
                                        link: '/third-party/3rd-party/products'
                                    },
                                    {
                                        title: t('Detail'),
                                        link: '#'
                                    }
                                ]}
                                pageTitle={productData?.productName || '...'}
                                translate={false}
                            />
                        </Col>
                    </Row>

                    {fetchProductDetailsQuery.isFetching ? (
                        <LoadingComponent />
                    ) : (
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardHeader className="bg-white">
                                        <h5 className="mb-1">
                                            {t('Product Information')}
                                        </h5>
                                        <p className="text-muted mb-0">
                                            {t('Displays the selected product along with its key identifiers, including lot number, internal ID, and description.')}
                                        </p>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            <Col sm='12' md={hasEnitityFeature ? '4' : '6'} className="mb-3">
                                                <Label>
                                                    {t('NACE Code')}
                                                </Label>
                                                <p>
                                                    {`${productData?.name?.class} - ${productData?.name?.name}`}
                                                </p>
                                            </Col>

                                            <Col sm='12' md={hasEnitityFeature ? '4' : '6'} className="mb-3">
                                                <Label>
                                                    {t('Product Name')}
                                                </Label>

                                                <p>
                                                    {productData?.productName}
                                                </p>
                                            </Col>

                                            {hasEnitityFeature && (
                                                <Col sm='12' md='4' className="mb-3">
                                                    <Label for="legal-entities">
                                                        {t("Legal Entities")}
                                                    </Label>

                                                    <p>
                                                        {(productData?.legal_entities || []).map((entity, index) => (
                                                            <span key={index}>
                                                                {entity.name}
                                                                {index < (productData?.legal_entities || []).length - 1 ? ', ' : ''}
                                                            </span>
                                                        ))}
                                                    </p>
                                                </Col>
                                            )}

                                            <Col sm='12' md="4" className="mb-3">
                                                <Label htmlFor="lotNumber">
                                                    {t('Lot Number')}
                                                </Label>
                                                <p>
                                                    {productData?.lotNumber}
                                                </p>
                                            </Col>

                                            <Col sm='12' md="4" className="mb-3">
                                                <Label htmlFor="internalProductId">
                                                    {t('Internal Product ID')}
                                                </Label>
                                                <p>
                                                    {productData?.internalProductId}
                                                </p>
                                            </Col>

                                            <Col sm='12' md='12' className="mb-3">
                                                <Label htmlFor="description">
                                                    {t('Description')}
                                                </Label>

                                                <p>
                                                    {productData?.description}
                                                </p>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col lg="12">
                                <Card>
                                    <CardHeader className="bg-white">
                                        <h5 className="mb-1">
                                            {t('Trade & Customs Details')}
                                        </h5>
                                        <p className="text-muted mb-0">
                                            {t('Shows the product’s import and export information, including dates, countries, and quantities, to trace its movement across borders.')}
                                        </p>
                                    </CardHeader>

                                    <CardBody>
                                        <Row>
                                            <Col sm='12' md="6" className="mb-3">
                                                <Label htmlFor="importDate">
                                                    {t('Date of Import')}
                                                </Label>
                                                <p>
                                                    {dateUtils.convertDateToDate(
                                                        productData?.importDate,
                                                        INTERNATIONAL_DATE_FORMAT
                                                    )}
                                                </p>
                                            </Col>
                                            <Col sm='12' md="6" className="mb-3">
                                                <Label htmlFor="importFromCountry">
                                                    {t('Import Country')}
                                                </Label>

                                                <p>
                                                    {t(productData?.importFromCountry?.name)}
                                                </p>
                                            </Col>

                                            <Col sm='12' md="6" className="mb-3">
                                                <Label htmlFor="exportDate">
                                                    {t('Date of Export')}
                                                </Label>

                                                <p>
                                                    {dateUtils.convertDateToDate(
                                                        productData?.exportDate,
                                                        INTERNATIONAL_DATE_FORMAT
                                                    )}
                                                </p>
                                            </Col>
                                            <Col sm='12' md="6" className="mb-3">
                                                <Label htmlFor="exportToCountry">
                                                    {t('Export Country')}
                                                </Label>

                                                <p>
                                                    {t(productData?.exportToCountry?.name)}
                                                </p>
                                            </Col>

                                            <Col sm='12' md="6" className="mb-3">
                                                <Label htmlFor="containerNumber">
                                                    {t('Container Number')}
                                                </Label>

                                                <i
                                                    className="ri-information-line me-1"
                                                    style={{
                                                        cursor: 'pointer'
                                                    }}
                                                    id="containerNumberHelpText"
                                                />

                                                <UncontrolledTooltip
                                                    target="containerNumberHelpText"
                                                >
                                                    {t('The unique identifier for the shipping container.')}
                                                </UncontrolledTooltip>

                                                <p>
                                                    {productData?.containerNumber}
                                                </p>
                                            </Col>
                                            <Col sm='12' md="6" className="mb-3">
                                                <Label htmlFor="shipmentNumber">
                                                    {t('Shipment Number')}
                                                </Label>

                                                <i
                                                    className="ri-information-line me-1"
                                                    style={{
                                                        cursor: 'pointer'
                                                    }}
                                                    id="shipmentNumberHelpText"
                                                />

                                                <UncontrolledTooltip
                                                    target="shipmentNumberHelpText"
                                                >
                                                    {t('The reference number for this shipment.')}
                                                </UncontrolledTooltip>

                                                <p>
                                                    {productData?.shipmentNumber}
                                                </p>
                                            </Col>

                                            <Col sm='12' md="3" className="mb-3">
                                                <Label htmlFor="metricTonnes">
                                                    {t('Quantity (Metric Tonnes)')}
                                                </Label>

                                                <p>
                                                    {productData?.metricTonnes}
                                                </p>
                                            </Col>

                                            <Col sm='12' md="3" className="mb-3">
                                                <Label htmlFor="hsCode">
                                                    {t('HS Code (6-digit)')}
                                                </Label>

                                                <p>
                                                    {productData?.hsCode}
                                                </p>
                                            </Col>
                                            <Col sm='12' md="3" className="mb-3">
                                                <Label htmlFor="knCode">
                                                    {t('KN Code (8-digit)')}
                                                </Label>

                                                <p>
                                                    {productData?.knCode}
                                                </p>
                                            </Col>
                                            <Col sm='12' md="3" className="mb-3">
                                                <Label htmlFor="taricCode">
                                                    {t('TARIC Code (10-digit)')}
                                                </Label>

                                                <p>
                                                    {productData?.taricCode}
                                                </p>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>

                            {commodity && (
                                <Col lg="12">
                                    <Card>
                                        <CardHeader className="bg-white">
                                            <h5 className="mb-1">
                                                {t('Due Diligence & Traceability')}
                                            </h5>
                                            <p className="text-muted mb-0">
                                                {t('Displays the Country of Origin, DDS Reference, and Verification numbers used to validate the product’s due diligence statement and ensure traceability for EUDR compliance.')}
                                            </p>
                                        </CardHeader>

                                        <CardBody>
                                            <Row>
                                                <Col sm='12' md="12" className="mb-3">
                                                    <Row>
                                                        <Col sm='12' md='6'>
                                                            <Label htmlFor="originCountry">
                                                                {t('Country of Origin')}
                                                            </Label>
                                                            <p>
                                                                {t(productData?.originCountry?.name)}
                                                            </p>
                                                        </Col>
                                                    </Row>
                                                </Col>

                                                <Col sm='12' md="6" className="mb-3">
                                                    <Label htmlFor="ddsReferenceNumber">
                                                        {t('DDS Reference Number')}
                                                    </Label>
                                                    <p>
                                                        {productData?.ddsReferenceNumber}
                                                    </p>
                                                </Col>
                                                <Col sm='12' md="6" className="mb-3">
                                                    <Label htmlFor="ddsVerificationNumber">
                                                        {t('DDS Verification Number')}
                                                    </Label>

                                                    <p>
                                                        {productData?.ddsVerificationNumber}
                                                    </p>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </Col>
                            )}

                            {commodity && (
                                <>
                                    {commodity !== 'cattle' ? (
                                        <Col lg="12">
                                            <Card>
                                                <CardHeader className="bg-white">
                                                    <h5 className="mb-1">
                                                        {t('Geolocation Data')}
                                                    </h5>
                                                    <p className="text-muted mb-0">
                                                        {t('Shows the mapped production area of the product, ensuring the plot is within the country of origin. This geolocation data supports EUDR compliance validation and traceability.')}
                                                    </p>
                                                </CardHeader>

                                                <CardBody>
                                                    <Row>
                                                        <Col sm='12' className="mb-3">
                                                            <Label>
                                                                {t('Geolocation (Draw Polygon)')}
                                                            </Label>

                                                            <GeoMap
                                                                name="geolocationReference"
                                                                readOnly={true}
                                                                preDefinedGeo={productData?.geolocationReference}
                                                                defaultSearchParam={productData?.originCountry?.label}
                                                                multiDraw={true}
                                                                styles={{
                                                                    height: '300px'
                                                                }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    ) : (
                                        <Col lg="12">
                                            <Card>
                                                <CardHeader className="bg-white">
                                                    <h5 className="mb-1">
                                                        {t('Geolocation Data')}
                                                    </h5>
                                                    <p className="text-muted mb-0">
                                                        {t('Displays the geolocation data for the entire production chain of cattle products, including the Breeding Farm, Fattening Farm, and Slaughterhouse. Each map shows the area of activity to provide full traceability and support EUDR compliance.')}
                                                    </p>
                                                </CardHeader>

                                                <CardBody>
                                                    <Row>
                                                        <Col sm='12' md="6" className="mb-3">
                                                            <Label>
                                                                {t('Breeding Farm Location')}
                                                            </Label>

                                                            <GeoMap
                                                                name="geolocationReference"
                                                                readOnly={true}
                                                                preDefinedGeo={productData?.geolocationReference}
                                                                defaultSearchParam={productData?.originCountry?.label}
                                                                multiDraw={true}
                                                                styles={{
                                                                    height: '300px'
                                                                }}
                                                            />
                                                        </Col>

                                                        <Col sm='12' md="6" className="mb-3">
                                                            <Label>
                                                                {t('Fattening Farm Location')}
                                                            </Label>

                                                            <GeoMap
                                                                name="farmGeolocationReference"
                                                                readOnly={true}
                                                                preDefinedGeo={productData?.farmGeolocationReference}
                                                                defaultSearchParam={productData?.originCountry?.label}
                                                                multiDraw={true}
                                                                styles={{
                                                                    height: '300px'
                                                                }}
                                                            />
                                                        </Col>

                                                        <Col sm='12' md="6" className="mb-3">
                                                            <Label>
                                                                {t('Slaughterhouse Location')}
                                                            </Label>

                                                            <GeoMap
                                                                name="slaughterGeolocationReference"
                                                                readOnly={true}
                                                                preDefinedGeo={productData?.slaughterGeolocationReference}
                                                                defaultSearchParam={productData?.originCountry?.label}
                                                                multiDraw={true}
                                                                styles={{
                                                                    height: '300px'
                                                                }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    )}
                                </>
                            )}
                        </Row>
                    )}
                </div>
            </Container>
        </div>
    )
}

export default withNamespaces()(
    ProductDetailPage
);